import { Injectable } from '@angular/core';

import type { Observable } from 'rxjs';
import { map } from 'rxjs';

import { HttpApiService } from '@recruitee/http';
import type { PaginationMeta } from '@recruitee/http-types';
import type { ID } from '@recruitee/ngrx';
import type {
  DataSource,
  DataSourceListItem,
  AnyOption,
  InputOptionsResponse,
  CreateViewPayload,
  FetchRecordsPayload,
  FilterPayload,
  LayoutRecordsCountPayload,
  UpdateViewPayload,
  View,
  ViewListItem,
  FetchGroupsPayload,
  GroupsQueryParams,
  GroupsResponse,
  ViewPositionPayload,
  RecordsResponse,
} from '@recruitee/views-types';
import { getOptions } from '@recruitee/walkthrough-utils';

import type { FilterGroupResponse } from '../store/filter-group/filter-group.store';
import type { RecordsQueryParams } from '../store/records/records.store';
import type { FetchSwimlanesPayload } from '../store/swimlanes/swimlanes.store';
import type { ViewCounter } from '../store/view-counters/view-counters.store';
import type { ViewResponse } from '../store/view/view.store';

@Injectable({ providedIn: 'root' })
export class ViewsHttpService {
  constructor(private httpApi: HttpApiService) {}

  public listDataSources(): Observable<DataSourceListItem[]> {
    return this.httpApi.get(`/views/data_sources`).pipe(map(({ dataSources }) => dataSources));
  }

  public fetchDataSource(dataSourceId: string): Observable<DataSource> {
    return this.httpApi
      .get(`/views/data_sources/${dataSourceId}`)
      .pipe(map(({ dataSource }) => dataSource));
  }

  public fetchFilterGroup(
    dataSourceId: string,
    filterGroupId: string,
    queryParams: { searchQuery?: string; page?: number; limit?: number },
  ): Observable<FilterGroupResponse> {
    return this.httpApi.get(`/views/${dataSourceId}/filter_groups/${filterGroupId}/filters`, {
      ...(queryParams.searchQuery && { search_query: queryParams.searchQuery }),
      ...(queryParams.page && { page: queryParams.page }),
      ...(queryParams.limit && { limit: queryParams.limit }),
    });
  }

  public fetchInputOptions(
    dataSourceId: string,
    filterId: string,
    inputId: string,
    filterValue: FilterPayload | null,
    queryParams?: Partial<Omit<PaginationMeta, 'totalCount'> & { searchQuery: string }>,
  ): Observable<InputOptionsResponse<AnyOption>> {
    const { searchQuery, ...query } = queryParams ?? {};
    return this.httpApi.post(
      `/views/${dataSourceId}/filters/${filterId}/inputs/${inputId}/options`,
      { ...query, search_query: searchQuery },
      filterValue ? { filters: [filterValue] } : null,
    );
  }

  public fetchViewsList(dataSourceId: string): Observable<ViewListItem[]> {
    return this.httpApi.get(`/views/${dataSourceId}/views`).pipe(map(({ views }) => views));
  }

  public updateViewsPositions(
    dataSourceId: string,
    positions: ViewPositionPayload[],
  ): Observable<ViewListItem[]> {
    return this.httpApi
      .post(`/views/${dataSourceId}/update_positions`, null, { positions })
      .pipe(map(({ views }) => views));
  }

  public fetchView(dataSourceId: string, viewId: number): Observable<ViewResponse> {
    return this.httpApi.get(`/views/${dataSourceId}/views/${viewId}`);
  }

  public createView(dataSourceId: string, data: CreateViewPayload): Observable<ViewResponse> {
    return this.httpApi.post(`/views/${dataSourceId}/views`, null, data);
  }

  public removeView(dataSourceId: string, viewId: number): Observable<ViewResponse> {
    return this.httpApi.delete(`/views/${dataSourceId}/views/${viewId}`);
  }

  public updateView(
    dataSourceId: string,
    viewPayload: UpdateViewPayload,
  ): Observable<ViewResponse> {
    return this.httpApi.patch(`/views/${dataSourceId}/views/${viewPayload.id}`, null, viewPayload);
  }

  public fetchGroups(
    dataSourceId: string,
    data: FetchGroupsPayload,
    queryParams?: GroupsQueryParams,
  ): Observable<GroupsResponse> {
    const { searchQuery, ...query } = queryParams ?? {};
    return this.httpApi.post(
      `/views/${dataSourceId}/groups`,
      { ...query, search_query: searchQuery },
      data,
    );
  }

  public fetchSwimlanes(
    dataSourceId: string,
    data: FetchSwimlanesPayload,
    queryParams?: GroupsQueryParams,
  ) {
    return this.httpApi.post(`/views/${dataSourceId}/swimlanes`, queryParams, data);
  }

  public fetchRecords(
    dataSourceId: string,
    data: FetchRecordsPayload,
    queryParams?: RecordsQueryParams,
  ): Observable<RecordsResponse> {
    return this.httpApi.post(
      `/views/${dataSourceId}/records`,
      queryParams,
      data,
      getOptions({ hasOnboarding: true }),
    );
  }

  public fetchLayoutRecordsCount(
    dataSourceId: string,
    data: LayoutRecordsCountPayload,
    queryParams?: RecordsQueryParams,
  ): Observable<RecordsResponse> {
    return this.httpApi.post(
      `/views/${dataSourceId}/records`,
      queryParams,
      data,
      getOptions({ hasOnboarding: true }),
    );
  }

  public fetchViewCounters(dataSourceId: string, ids: ID[]) {
    return this.httpApi
      .get(
        `/views/${dataSourceId}/counters`,
        { ids },
        {
          headers: {
            'X-Json-Accent': [],
          },
        },
      )
      .pipe(
        map(({ counters }) =>
          Object.entries(counters).map(
            ([id, count]) =>
              <ViewCounter>{
                id: Number.parseInt(id, 10) || id,
                count,
              },
          ),
        ),
      );
  }

  public saveSnapshot(
    dataSourceId: string,
    viewId: View['id'],
    codename: View['codename'],
    form: CreateViewPayload,
  ): Observable<void> {
    const id = codename ? { codename } : { viewId };
    return this.httpApi.post(`/views/${dataSourceId}/views_snapshots`, null, {
      form,
      ...id,
    });
  }

  public clearSnapshot(
    dataSourceId: string,
    viewId: View['id'],
    codename: View['codename'],
  ): Observable<void> {
    const query = codename ? { codename } : { view_id: viewId };
    return this.httpApi.delete(`/views/${dataSourceId}/views_snapshots`, query);
  }
}
